// ═══════════════════════════════════════════════════════════════════
// SURVEILLANCE ROUTE TYPES — V4 post-refactor 2026-04-22
//
// Schema alineado al patrón canónico GenericOrder/Action/Accident:
// - SR: sin scheduledStartDate/actualStartDate/actualEndDate (→Order.openingDate/
//   registerInitDate/registerEndDate), sin description (→Order.description),
//   sin startedBy/endedBy (→Order.user), sin createdAt/updatedAt, sin segments[]
//   (relación vive en Order parent hierarchy). operators → users.
// - RouteSegment: sin segmentNumber (reemplazado por indexType), sin description
//   (→Order.description), sin startedBy/endedBy (→Order.user), sin surveillanceRouteId
//   (la relación RV→tramos vive en Order.parentExpeditionOrderIDs).
// ═══════════════════════════════════════════════════════════════════

// V4 D5: estados del RV. Subconjunto de OrderState del backend;
// viven en el campo `state` unificado (no en un campo separado).
export enum RouteState {
  OPEN = 'open',
  IN_PROGRESS = 'in-progress',
  PAUSED = 'paused',
  PENDING_VALIDATION = 'pending-validation',
  END = 'end',
  ANNULLED = 'annulled',
}

// V4 D7: 3 causas de pausa.
export enum PauseCause {
  BASIC = 'basic',
  ACTION = 'action',
  EXTERNAL_CALL = 'external-call',
}

export type RouteGpsPoint = {
  id: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  accuracy?: number;
};

/** Marca personal de «revisado» de un tramo; `stale` = el tramo cambió tras marcarlo. */
export type SegmentReviewState = { reviewedAt: string; stale: boolean };

/**
 * Timer del recorrido (backend Prisma `RouteSegment`).
 * Cada timer es un intervalo atómico de trabajo con PKs al inicio/fin.
 */
export type RouteSegment = {
  id: string;
  indexExpedition?: number;
  indexType?: number;
  state: string;
  direction: string;
  directionName?: string;
  lane?: string;
  /**
   * Carretera del tramo (multi-carretera 2026-08-15). `null`/ausente = la
   * PRIMERA del recorrido, que es la que el tramo tenía implícita antes de
   * esto. Sin este dato el PK es ambiguo: la C-25 y su ramal C-25z comparten
   * kilometraje sobre trazados que se separan hasta 2,76 km.
   */
  roadId?: string | null;
  /**
   * Por qué carreteras pasó el tramo, en orden. Vacío = una sola, la de
   * `roadId`. 🔴 Con trozos, `pkEnd - pkInit` NO es una distancia: los PK de dos
   * carreteras no son comparables. Usa `segmentMeters`.
   */
  roadStretches?: RouteRoadStretch[] | null;
  pkInitKm: number;
  pkInitMeter: number;
  // pkEnd null mientras el timer está activo
  pkEndKm?: number | null;
  pkEndMeter?: number | null;
  initLatitude?: number;
  initLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  startDate: string;
  endDate?: string | null;
  // Pausa V4 D7
  pauseCause?: string | null;
  pauseCauseDetail?: string | null;
  pendingExternalLink: boolean;
  /** P15 Fase 2: cerró con causa `action` y todavía no tiene su Actuació básica. */
  pendingActionLink: boolean;
  /** Mi marca de revisado (solo en el fragmento de detalle); `null` = sin marca o rol sin permiso. */
  myReview?: SegmentReviewState | null;
  creationDate?: string;
  registerInitDate?: string;
  // Entidad doble con Order. Invariante: length === 1.
  // Cross-links (D2 basic action / D4 external) viven en la Order del tramo
  // (self-ref `segmentLinkedBasicActionOrderIDs`/`segmentLinkedExternalOrderIDs`),
  // no en el RouteSegment.
  orderIDs: string[];
  /**
   * Recorrido PADRE del tramo (2026-08-11), resuelto en lote por el backend.
   * Opcional: un backend anterior a este campo no lo devuelve, y el consumidor
   * cae al código `Su.NNNN` derivado del ROID.
   */
  surveillanceRoute?: {
    id: string;
    name?: string | null;
    indexExpedition?: number | null;
    /** Solo en el modal «Vincular a recorrido» (columna Carretera, 2026-09-23). */
    roadIDs?: string[] | null;
  } | null;
  // D3 (2026-07-23): vínculos resueltos en backend (@ResolveField batched) — solo
  // llegan vía el fragment detalle de GET_ROUTE_SEGMENTS. `actionId` = id de la
  // Action entity (nav de la básica). En externa, `type` = generic|campaign.
  linkedBasicActionOrder?: {
    id: string;
    roid?: string | null;
    type?: string | null;
    state?: string | null;
    actionId?: string | null;
  } | null;
  linkedExternalOrder?: {
    id: string;
    roid?: string | null;
    type?: string | null;
    state?: string | null;
  } | null;
  /**
   * «Registrar parada» (2026-10-06): rastro de la partición si gestión partió
   * este tramo. Solo llega en el fragment de detalle. `undoable` lo calcula el
   * servidor con la misma regla que `undoSegmentSplit`.
   */
  stopSplit?: SegmentStopSplit | null;
  /**
   * «Transferir tramo» (2026-10-07): de dónde vino si gestión lo cambió de
   * recorrido. Solo llega en el fragment de detalle.
   */
  transfer?: SegmentTransfer | null;
};

/** Rastro de «Transferir tramo» (spec 2026-10-07): el último traspaso. */
export type SegmentTransfer = {
  fromRouteId: string;
  fromRouteRoid: string;
  fromRoid: string;
  at: string;
  byUserId?: string | null;
  /** Nombre de quien lo hizo, si es del tenant (30-oct). */
  byName?: string | null;
};

/** Un recorrido al que se podría transferir el tramo (lo calcula el servidor). */
export type SegmentTransferTarget = {
  routeId: string;
  roid: string;
  name?: string | null;
  state: string;
  firstStart?: string | null;
  lastEnd?: string | null;
  segmentsCount: number;
  fits: boolean;
  reasonCode?: string | null;
  /**
   * Cabe, pero sus PK no siguen al tramo de al lado (decisión del 15-oct: no
   * bloquea): se avisa para revisarlos después con «Corregir».
   */
  pkWarningCode?: string | null;
  prevEnd?: string | null;
  nextStart?: string | null;
};

export type TransferRouteSegmentResult = {
  segment: RouteSegment;
  roid: string;
  unlinkedBasicOrderIds: string[];
  /**
   * Básicas que se han quedado a 0 min al rehacer su fin (su fin real no se
   * sabe): gestión tiene que editarlo. Opcional: una respuesta antigua no lo
   * trae.
   */
  zeroLengthBasics?: { id: string; roid?: string | null }[];
  /**
   * Gemelas del recorrido de origen y del de destino: sus fechas cambian y el
   * listado de recorridos sale de ellas (QA 15-oct, T2.2). Opcional: una
   * respuesta antigua no lo trae.
   */
  routeOrderIds?: string[];
};

/** «Mover tramos» (plan 2026-10-15 § 3): lo que se manda. */
export type MoveRouteSegmentsInput = {
  sourceRouteId: string;
  /** Tramos seguidos de la tabla de Tramos. */
  segmentIds: string[];
  /** Uno de los dos: un recorrido que ya existe o uno nuevo. */
  targetRouteId?: string | null;
  newRoute?: {
    name?: string | null;
    userIDs: string[];
    vehicleAssignments: { vehicleId: string; driverId: string }[];
  } | null;
  /** Si el origen se queda sin tramos, anularlo. */
  annulEmptySource?: boolean;
  clientOpId: string;
};

export type MoveRouteSegmentsResult = {
  sourceRouteId: string;
  sourceRoid: string;
  targetRouteId: string;
  targetRoid: string;
  movedCount: number;
  sourceAnnulled: boolean;
  zeroLengthBasics: { id: string; roid?: string | null }[];
  /** Básicas que cambiaron de recorrido y de ROID (para la caché). */
  movedBasicOrderIds?: string[];
};

/** Rastro de «Registrar parada» en un tramo (spec 2026-10-06). */
export type SegmentStopSplit = {
  operationId: string;
  role: 'original' | 'piece' | string;
  byUserId?: string | null;
  /** Nombre de quien lo hizo, si es del tenant (30-oct). */
  byName?: string | null;
  at: string;
  undoable: boolean;
};

/** Una parada que el GPS crudo de un tramo deja ver (solo propuesta). */
export type SegmentStopSuggestion = {
  startDate: string;
  endDate: string;
  minutes: number;
  latitude: number;
  longitude: number;
  pkKm: number | null;
  pkMeter: number | null;
  roadId: string | null;
  onRoad: boolean;
  position: 'start' | 'middle' | 'end';
  candidateOrder: {
    id: string;
    roid?: string | null;
    type?: string | null;
    state?: string | null;
  } | null;
  candidatesCount: number;
};

/** Lo que el modal manda por cada parada (input `SegmentStopInput`). */
export type SegmentStopInput = {
  startDate: string;
  endDate: string;
  pkKm: number;
  pkMeter: number;
  roadId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  pauseCause?: string | null;
  pauseCauseDetail?: string | null;
  directionAfter?: string | null;
  directionNameAfter?: string | null;
};

export type SplitRouteSegmentResult = {
  segments: RouteSegment[];
  links: {
    segmentId: string;
    reason: 'linked' | 'ambiguous' | 'no-candidate' | 'already-linked' | string;
    order: { id: string; roid?: string | null; type?: string | null } | null;
  }[];
};

export type SurveillanceRoute = {
  id: string;
  indexExpedition?: number;
  indexType?: number;
  state: RouteState | string;
  name?: string;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
  creationDate?: string;
  registerInitDate?: string;
  // Force-close del gestor (rv-recorrido-colgado 2026-07-22): quién/cuándo forzó
  // el cierre de un recorrido colgado. Nulos si fue un finish normal del Tablet.
  forceClosedById?: string | null;
  forceClosedAt?: string | null;
  // Entidad doble con Order. Invariante: length === 1.
  orderIDs: string[];
  concessionIDs: string[];
  roadIDs: string[];
  /** Rango de PK por carretera (multi-carretera 2026-08-15). Vacío = recorrido
   *  de una sola carretera, cuyo rango son los `pkInit*`/`pkEnd*` de la propia
   *  entidad. */
  roadRanges?: RouteRoadRangeInput[];
  teamIDs?: string[];
  capitolIDs?: string[];
  subCapitolIDs?: string[];
  predefinedOrderIDs?: string[];
  // Participantes (patrón Action — `users` en vez de `operators`)
  /** CREADOR del recorrido: la tablet que lo creó (denormalizado, filtro "Creador"). Distinto de `userIDs` (la cuadrilla) y de `timerOwnerId` (dueño actual del timer). */
  userId?: string | null;
  /** Dueño RECLAMABLE del timer: la tablet que lo tiene AHORA (se reasigna al que retoma el RV). Lo usan `isOwnSurveillanceRoute`/`canOperateTimer`. */
  timerOwnerId?: string | null;
  userIDs?: string[];
  vehicleOccupantIDs?: string[];
  gpsPoints?: RouteGpsPoint[];
  // V4 post-refactor 2026-04-22: `segments` se resuelve via ResolveField backend
  // (la relación RV↔tramos vive en Order.parentExpeditionOrderIDs).
  segments?: RouteSegment[];
  /** Cross-link 3 (2026-09-22): OTs vinculadas al recorrido, con procedencia. Solo en el fragment de detalle. */
  linkedExternalOrders?: LinkedExternalOrder[];
  /**
   * Prevención P3/P4 (2026-10-15): aviso de turno que calcula el servidor al
   * leer. Solo lo piden el listado y el detalle (ver `ShiftWarning`).
   */
  shiftWarning?: ShiftWarning | null;
  /**
   * Prevención P1 (2026-10-15): fin del último tramo CERRADO. Solo lo pide el
   * sondeo de activos del cronómetro, que lo deja en caché para usarlo sin red.
   */
  lastSegmentEndDate?: string | null;
  /**
   * QA T6b (2026-10-15): cuentas con un tramo ABIERTO en el recorrido (lo que
   * mira el back para «cronómetro activo en otro recorrido»). `null` = no se
   * sabe (caché de antes o lectura fallida). Solo lo pide el sondeo de la
   * tablet.
   */
  openSegmentUserIds?: string[] | null;
};

/**
 * Aviso de turno del recorrido (prevención P3/P4, plan 2026-10-15 § 5):
 * - `span-over-24h`: más de 24 h entre su primer y su último tramo; Gestión
 *   Horaria no cuenta sus horas.
 * - `paused-too-long`: sigue pausado y su último tramo acabó hace más de 14 h.
 */
export type ShiftWarning = 'paused-too-long' | 'span-over-24h';

/** Tramo ligado a una OT externa (cross-link 2), visto desde el recorrido o la OT. */
export type LinkedSegmentRef = {
  id: string;
  indexType?: number | null;
  pauseCause?: string | null;
  endDate?: string | null;
};

/** Una OT externa vinculada a un recorrido (cross-link 3), con procedencia. */
export type LinkedExternalOrder = {
  order: {
    id: string;
    roid?: string | null;
    type?: string | null;
    state?: string | null;
    actionId?: string | null;
  };
  /** 'auto' (al registrar una actuación en la OT) | 'manual' */
  source: string;
  linkedAt?: string | null;
  viaOrderId?: string | null;
  segments: LinkedSegmentRef[];
};

/**
 * Un recorrido vinculado a una OT genérica/campaña (cross-link 3): lo que pinta
 * su pill «Vinculado con Su.NN». La procedencia y los tramos se leen en el
 * recorrido (23/09: fuera el bloque de detalle de la OT, redundante con las pills).
 */
export type LinkedRouteRef = {
  id: string;
  orderId: string;
  roid?: string | null;
  name?: string | null;
  state: string;
  linkedAt?: string | null;
  /** Spec 2026-09-23: hijas de la OT que hizo la patrulla de este recorrido. */
  workOrderIds?: string[];
};

/**
 * Spec § 16: un trabajo de la OT y la patrulla que lo hizo, cuyo recorrido NO
 * está ligado a la OT. `routeId` es la entidad (navegar, ligar).
 */
export type UnlinkedWorkRouteRef = {
  workOrderId: string;
  routeId: string;
  routeOrderId: string;
  roid?: string | null;
  /**
   * Solo en el front (`unlinkedRoutesForWork`): cuántos trabajos de la OT hizo
   * ese recorrido. Ligar es de la OT entera, así que se ligan todos a la vez.
   */
  workCount?: number;
};

/** Recorrido al que se atribuye una actuación o accidente (spec 2026-09-23). */
export type WorkedInRouteRef = {
  id: string;
  roid?: string | null;
  name?: string | null;
};

// ═══════════════════════════════════════════════════════════════════
// INPUT TYPES
// ═══════════════════════════════════════════════════════════════════

export type FilterSurveillanceRouteList = {
  take?: number;
  cursor?: string;
  orderBy?: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  concessionId?: string;
  state?: string;
  teamId?: string;
  capitolId?: string;
  subCapitolId?: string;
  predefinedOrderId?: string;
  indexExpedition?: number;
  indexType?: number;
  pkInitKm?: number;
  pkInitMeter?: number;
  pkEndKm?: number;
  pkEndMeter?: number;
};

/**
 * Un trozo de TRAMO recorrido por una carretera (2026-08-17). Sus PK van
 * medidos en esa vía: sumar los trozos da la distancia, restar los extremos del
 * tramo no.
 */
export type RouteRoadStretch = {
  roadId: string;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
};

/**
 * Hasta dónde llega el RECORRIDO en una de sus carreteras (multi-carretera
 * 2026-08-15). Un PK no identifica un sitio por sí solo: la C-25 y su ramal
 * C-25z comparten kilometraje sobre trazados que se separan hasta 2,76 km, así
 * que el PK 235+000 existe en las dos y son puntos a 874 m.
 */
export type RouteRoadRangeInput = {
  roadId: string;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
};

/**
 * Plantilla de recorrido (2026-08-16): el ámbito fijo que un turno recorre cada
 * día. El asistente la ofrece en el selector de nombre y, al elegirla, siembra
 * nombre + carreteras + PK.
 *
 * 🔴 Aquí SIEMPRE hay un rango por carretera, también cuando solo hay una — al
 * revés que en el recorrido, donde con una sola el rango son sus propios
 * `pkInit*`/`pkEnd*`. La plantilla no tiene ningún recorrido donde caer.
 */
export type SurveillanceRoutePreset = {
  id: string;
  name: string;
  isActive: boolean;
  roadIDs: string[];
  roadRanges: RouteRoadRangeInput[];
};

export type NewSurveillanceRoute = {
  name?: string;
  // `description` se guarda en Order.description (entidad doble).
  description?: string;
  concessionId: string;
  /** Multi-carretera (2026-08-15): `roadIds` gana si llega; `roadId` se
   *  conserva para los clientes de una sola. */
  roadId?: string;
  roadIds?: string[];
  roadRanges?: RouteRoadRangeInput[];
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
  // V4 post-refactor 2026-04-22: nombres canónicos.
  openingDate?: string;
  orderId?: string;
  teamId?: string;
  capitolId?: string;
  subCapitolId?: string;
  predefinedOrderId?: string;
  userIDs?: string[];
  vehicleOccupantIDs?: string[];
  // Tour P5 (2026-07-07): parejas vehículo→conductor del wizard — el backend
  // crea los VehicleOccupant dentro de la $transaction del create.
  vehicleAssignments?: { vehicleId: string; driverId: string }[];
};

export type UpdateSurveillanceRoute = {
  id: string;
  name?: string;
  description?: string;
  pkInitKm?: number;
  pkInitMeter?: number;
  pkEndKm?: number;
  pkEndMeter?: number;
  openingDate?: string;
};

export interface SaveSurveillanceRouteInput {
  id?: string;
  clientOpId?: string;
  name?: string;
  concessionId?: string;
  /** Multi-carretera (2026-08-15). Ausente = no tocar; el backend rechaza una
   *  lista vacía (un recorrido no puede quedarse sin carretera). */
  roadId?: string;
  roadIds?: string[];
  roadRanges?: RouteRoadRangeInput[];
  pkInitKm?: number;
  pkInitMeter?: number;
  pkEndKm?: number;
  pkEndMeter?: number;
  teamIds?: string[];
  description?: string;
  notes?: string;
  openingDate?: string;
  userIDs?: string[];
  vehicleOccupantIDs?: string[];
  vehicleAssignments?: Array<{ vehicleId: string; driverId: string }>;
}

export type StartSurveillanceRoute = {
  id: string;
  registerInitDate: string;
};

export type FinishSurveillanceRoute = {
  id: string;
  registerEndDate?: string;
};

/**
 * Input para crear un nuevo timer (RouteSegment).
 */
export type NewRouteSegment = {
  surveillanceRouteId: string;
  /**
   * Multi-carretera (2026-08-15): en qué carretera del recorrido empieza el
   * tramo. Ausente = la primera, que es lo que tenían implícito los tramos de
   * antes. Sin este campo el operario podía elegir carretera en el modal y el
   * tramo nacía igualmente en la primera — con el PK juzgado contra la
   * equivocada durante todo su ciclo de vida.
   */
  roadId?: string;
  direction: string;
  directionName?: string;
  pkInitKm: number;
  pkInitMeter: number;
  initLatitude?: number;
  initLongitude?: number;
  description?: string;
};

export type UpdateRouteSegment = {
  id: string;
  direction?: string;
  directionName?: string;
  description?: string;
};

/**
 * Input para cerrar un timer.
 */
export type FinishRouteSegment = {
  id: string;
  /**
   * Por qué carreteras pasó el tramo. Lo calcula el cliente del rastro GPS al
   * cerrar. Ausente = no tocar lo guardado (el cierre interno del recorrido y
   * los replays de la cola no lo mandan).
   */
  roadStretches?: RouteRoadStretch[];
  endDate?: string;
  pkEndKm: number;
  pkEndMeter: number;
  endLatitude?: number;
  endLongitude?: number;
  pauseCause?: string;
  pauseCauseDetail?: string;
};

export type AddGpsPoint = {
  surveillanceRouteId: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  accuracy?: number;
};

export type AddGpsPointsBatch = {
  surveillanceRouteId: string;
  points: Omit<AddGpsPoint, 'surveillanceRouteId'>[];
};

// ═══════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════

export type SurveillanceRouteListResponse = {
  surveillanceRoutes: SurveillanceRoute[];
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

/** Trabajo (actuación o accidente) que atendió el aviso a sala de un tramo. */
export interface LinkedWorkRef {
  id: string;
  roid?: string | null;
  type?: string | null;
  state?: string | null;
  actionId?: string | null;
  accidentId?: string | null;
}

/** id del RouteSegment → trabajos que atendieron su aviso. */
export type ExternalWorksBySegment = Record<string, LinkedWorkRef[]>;
